/**
 * bridge/invoke_controller.js — declarative native-capability calls from markup.
 *
 * The generic wiring layer for the bridge library. Every other controller in this
 * folder OWNS a capability (registers its three transports); this one owns none —
 * it lets the SERVER decide, per element, which capability fires and when,
 * without a bespoke controller or a line of page JS per behaviour.
 *
 * Identifier "bridge--invoke" (glob-derived from this subdir). Plain Stimulus,
 * NOT a BridgeComponent: it has nothing to send over Strada, and extending
 * BridgeComponent would UA-gate connect() so the whole thing would go dead in the
 * browser and on Android.
 *
 * Template wiring — the whole API:
 *   <button data-controller="bridge--invoke"
 *           data-bridge-call="haptic"
 *           data-bridge-args='["success"]'>Mark paid</button>
 *
 *   data-bridge-call   capability path on window.ChippieBridge. Dotted for
 *                      namespaced ones: "localNotification.schedule".
 *   data-bridge-args   JSON ARRAY of positional args (optional; default []).
 *                      It is an array, not an object, because the facade spreads
 *                      it — a single-object capability takes '[{...}]'.
 *   data-bridge-on     what triggers it (optional; default "click"). Any DOM
 *                      event name, or the literal "connect" to fire as soon as
 *                      the element enters the DOM.
 *
 * "connect" is the server-push-in-markup case: a page or Turbo Frame can carry
 * <div data-controller="bridge--invoke" data-bridge-call="haptic" data-bridge-on="connect">
 * and the device responds the moment that fragment lands. It routes through
 * ChippieBridge.remote(), so it is limited to REMOTE_ALLOWED — there is no user
 * gesture behind an element simply appearing. Every other trigger is a real
 * interaction and goes through ChippieBridge.invoke() with no whitelist.
 *
 * Failure is always silent-but-logged (see bridge-commands.js). An element wired
 * to a capability this runtime lacks does nothing; it never throws and never
 * blocks the click's other handlers.
 *
 * <!-- JavaScript: static/js/bridge-commands.js (invoke/remote live there) -->
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    const trigger = this.element.dataset.bridgeOn || "click";

    if (trigger === "connect") {
      this.#run();
      return;
    }

    // Saved bound ref so disconnect() can remove it — Turbo replaces <body> on
    // every navigation and unremoved listeners accumulate on persistent elements.
    this._onTrigger = () => this.#run();
    this._triggerName = trigger;
    this.element.addEventListener(trigger, this._onTrigger);
  }

  disconnect() {
    if (this._onTrigger) {
      this.element.removeEventListener(this._triggerName, this._onTrigger);
      this._onTrigger = null;
    }
  }

  #run() {
    if (!window.ChippieBridge || !window.ChippieBridge.invoke) return;

    const call = this.element.dataset.bridgeCall;
    if (!call) {
      console.warn("[bridge--invoke] element has no data-bridge-call", this.element);
      return;
    }

    let args = [];
    const raw = this.element.dataset.bridgeArgs;
    if (raw) {
      try {
        args = JSON.parse(raw);
      } catch (_e) {
        console.warn("[bridge--invoke] data-bridge-args is not valid JSON:", raw);
        return;
      }
    }

    // No gesture behind a connect-triggered call, so it takes the gated path.
    if (this.element.dataset.bridgeOn === "connect") {
      window.ChippieBridge.remote(call, args);
    } else {
      window.ChippieBridge.invoke(call, args);
    }
  }
}
